<template>
	<div id="clientDns" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="SEO" />
		<div class="seo-deprecated mt-3">
			<font-awesome-icon class="me-2" :icon="['fas', 'exclamation-triangle']" />
			<span><strong>Heads up —</strong> this SEO editor is being deprecated and will be removed in a future release. Please avoid relying on it for new work.</span>
		</div>
		<div class="seo-top-note mt-3">
			Here we adjust SEO for models that require the best possible SEO optimization.
		</div>

		<!-- SEO Models -->
		<div class="seo-models mt-3 d-flex flex-wrap">
			<button @click="selectModel('page')" class="seo-model-btn me-2" :class="{ active: selectedModel && selectedModel == 'page' }">
				Pages
			</button>
			<button @click="selectModel('news-notice')" class="seo-model-btn me-2" :class="{ active: selectedModel && selectedModel == 'news-notice' }">
				News Notice
			</button>
		</div>

		<!-- SEO Model for Pages -->
		<div v-if="selectedModel == 'page'" class="seo-model-active row mt-3">
			<div class="col-md-4">
				<div class="seo-list-panel">
				<div class="seo-search mb-3">
					<font-awesome-icon :icon="['fas', 'search']" class="seo-search__icon" />
					<input type="text" class="seo-search__input" placeholder="Search pages…" v-model.trim="search" />
					<button v-if="search" type="button" class="seo-search__clear" aria-label="Clear search" @click="search = ''">
						<font-awesome-icon :icon="['fas', 'times']" />
					</button>
				</div>
				<div class="items-wrapper">
					<div v-if="!filteredPages.length" class="seo-empty">No pages match "{{ search }}".</div>
					<div
						class="seo-item"
						:class="{ active: selectedObject && selectedObject.id == page.id }"
						@click="manageSeo(page)"
						v-for="page in filteredPages"
						:key="page.id"
					>
						<div class="model-item d-flex justify-content-between align-items-center">
							<div class="model-descriptions">
								<h5 class="mb-0">{{ page.title }}</h5>
								<code style="font-size: 9px;">{{ page.slug }} </code>
							</div>
							<div class="model-actions d-flex justify-content-center align-items-center">
								<font-awesome-icon v-if="page.seo" style="color:#34a853" :icon="['fab', 'google']" />
							</div>
						</div>
					</div>
				</div>
				</div>
			</div>
			<div v-if="selectedSeoData" class="col-md-8">
				<div class="seo-section">
				<div class="mb-3" v-if="selectedObject">
					<div class="d-flex justify-content-between align-items-center">
						<div>
							<h4 class="m-0 p-0">{{ selectedObject.title }}</h4>
							<span>Slug: </span><code>{{ selectedObject.slug }} </code>
						</div>
						<div>
							<button @click="displayContent()" class="btn btn-secondary btn-sm"><font-awesome-icon :icon="['fas', 'file']" /> Show content</button>
						</div>
					</div>
				</div>
				<div class="form-group mb-3">
					<label for="seoTitle">SEO Title</label>
					<input type="text" id="seoTitle" class="form-control" v-model="selectedSeoData.title" />
				</div>
				<div class="form-group mb-3">
					<div class="d-flex justify-content-between align-items-center mb-2">
						<div>
							<label for="seoDescription">SEO Description</label>
						</div>
						<div class="actions">
							<button @click="generateDescriptionWithAI()" class="btn btn-warning btn-sm">
								<font-awesome-icon
									:icon="[aiDescriptionLoading ? 'fas' : 'fas', aiDescriptionLoading ? 'spinner' : 'magic']"
									:class="[aiDescriptionLoading ? 'fa-spin' : '']"
								/>
								Generate with AI
							</button>
						</div>
					</div>
					<textarea id="seoDescription" rows="4" :disabled="aiDescriptionLoading" class="form-control" v-model="selectedSeoData.description" />
				</div>
				<div class="form-group mb-3">
					<div class="d-flex justify-content-between align-items-center">
						<div>
							<label for="seoKeywords">SEO Keywords</label>
							<p class="p-0 m-0" style="font-size: 12px;"><code>Separate keywords with comma (,)</code></p>
						</div>
						<div class="actions">
							<button @click="generateKeywordsWithAI()" class="btn btn-warning btn-sm">
								<font-awesome-icon
									:icon="[aiKeywordsLoading ? 'fas' : 'fas', aiKeywordsLoading ? 'spinner' : 'magic']"
									:class="[aiKeywordsLoading ? 'fa-spin' : '']"
								/>
								Generate with AI
							</button>
						</div>
					</div>
					<textarea id="seoKeywords" rows="4" :disabled="aiKeywordsLoading" class="form-control" v-model="selectedSeoData.keywords" />
				</div>
				<p class="p-0 m-0 mb-1">Google search preview</p>
				<div class="google-search-preview bg-white">
					<div class="search-result">
						<a href="#" class="title">{{ selectedSeoData.title }}</a>
						<p class="url">{{ client.url }}/{{ selectedObject.slug }}</p>
						<p class="description">{{ selectedSeoData.description }}</p>
					</div>
				</div>
				<hr>
				<div class="form-group mb-3">
					<label for="ogTitle">OG Title</label>
					<input type="text" id="ogTitle" class="form-control" v-model="selectedSeoData.ogTitle" />
				</div>
				<div class="form-group mb-3">
					<div class="mb-2">
						<label for="ogDescription">OG Description</label>
					</div>
					<textarea id="ogDescription" rows="4" class="form-control" v-model="selectedSeoData.ogDescription" />
				</div>
				<div class="form-group mb-3">
					<label for="ogImage">OG Image</label>
					<input type="text" id="ogImage" class="form-control" v-model="selectedSeoData.ogImage" />
				</div>
				<div class="form-group mb-3">
					<label for="ogSiteName">OG Site Name</label>
					<input type="text" id="ogSiteName" class="form-control" v-model="selectedSeoData.ogSiteName" />
				</div>
				<div class="d-flex justify-content-end align-items-center">
					<button v-if="seoOperation == 'Create'" @click="storeSeo(selectedObject.id, 'App\\Models\\Page')" class="btn btn-success mb-3">
						<font-awesome-icon v-if="saveLoading" :icon="['fas', 'spinner']" :class="['fa-spin', 'ms-2']" :disabled="saveLoading" />{{ seoOperation }}
					</button>
					<button v-if="seoOperation == 'Update'" @click="updateSeo(selectedObject, 'App\\Models\\Page')" class="btn btn-primary mb-3">
						<font-awesome-icon v-if="saveLoading" :icon="['fas', 'spinner']" :class="['fa-spin', 'ms-2']" :disabled="saveLoading" />{{ seoOperation }}
					</button>
				</div>
				</div>
			</div>
			<div class="col-md-8 text-warning d-flex justify-content-center align-items-start mt-5" v-else>
				<h4>Select page in order to make changes</h4>
			</div>
		</div>

		<!-- SEO Model for News Notice -->
		<div v-if="selectedModel == 'news-notice'" class="seo-model-active row mt-3">
			<div class="col-md-4">
				<div class="seo-list-panel">
				<div class="seo-search mb-3">
					<font-awesome-icon :icon="['fas', 'search']" class="seo-search__icon" />
					<input type="text" class="seo-search__input" placeholder="Search news & notices…" v-model.trim="search" />
					<button v-if="search" type="button" class="seo-search__clear" aria-label="Clear search" @click="search = ''">
						<font-awesome-icon :icon="['fas', 'times']" />
					</button>
				</div>
				<div class="items-wrapper">
					<div v-if="!filteredNewsNotices.length" class="seo-empty">No news &amp; notices match "{{ search }}".</div>
					<div
						class="seo-item"
						:class="{ active: selectedObject && selectedObject.id == news.id }"
						@click="manageSeo(news)"
						v-for="news in filteredNewsNotices"
						:key="news.id"
					>
						<div class="model-item d-flex justify-content-between align-items-center">
							<div class="model-descriptions">
								<h5 class="mb-0">{{ news.title }}</h5>
								<code style="font-size: 9px;">{{ news.slug }}</code>
							</div>
							<div class="model-actions d-flex justify-content-center align-items-center">
								<font-awesome-icon v-if="news.seo" style="color:#34a853" :icon="['fab', 'google']" />
							</div>
						</div>
					</div>
				</div>
				</div>
			</div>
			<div v-if="selectedSeoData" class="col-md-8">
				<div class="seo-section">
				<div class="mb-3" v-if="selectedObject">
					<div class="d-flex justify-content-between align-items-center">
						<div>
							<h4 class="m-0 p-0">{{ selectedObject.title }}</h4>
							<span>Slug: </span><code>{{ selectedObject.slug }} </code>
						</div>
						<div>
							<button @click="displayContent()" class="btn btn-secondary btn-sm"><font-awesome-icon :icon="['fas', 'file']" /> Show content</button>
						</div>
					</div>
				</div>
				<div class="form-group mb-3">
					<label for="seoTitle">SEO Title</label>
					<input type="text" id="seoTitle" class="form-control" v-model="selectedSeoData.title" />
				</div>
				<div class="form-group mb-3">
					<div class="d-flex justify-content-between align-items-center mb-2">
						<div>
							<label for="seoDescription">SEO Description</label>
						</div>
						<div class="actions">
							<button @click="generateDescriptionWithAI()" class="btn btn-warning btn-sm">
								<font-awesome-icon
									:icon="[aiDescriptionLoading ? 'fas' : 'fas', aiDescriptionLoading ? 'spinner' : 'magic']"
									:class="[aiDescriptionLoading ? 'fa-spin' : '']"
								/>
								Generate with AI
							</button>
						</div>
					</div>
					<textarea id="seoDescription" rows="4" :disabled="aiDescriptionLoading" class="form-control" v-model="selectedSeoData.description" />
				</div>
				<div class="form-group mb-3">
					<div class="d-flex justify-content-between align-items-center">
						<div>
							<label for="seoKeywords">SEO Keywords</label>
							<p class="p-0 m-0" style="font-size: 12px;"><code>Separate keywords with comma (,)</code></p>
						</div>
						<div class="actions">
							<button @click="generateKeywordsWithAI()" class="btn btn-warning btn-sm">
								<font-awesome-icon
									:icon="[aiKeywordsLoading ? 'fas' : 'fas', aiKeywordsLoading ? 'spinner' : 'magic']"
									:class="[aiKeywordsLoading ? 'fa-spin' : '']"
								/>
								Generate with AI
							</button>
						</div>
					</div>
					<textarea id="seoKeywords" rows="4" :disabled="aiKeywordsLoading" class="form-control" v-model="selectedSeoData.keywords" />
				</div>
				<p class="p-0 m-0 mb-1">Google search preview</p>
				<div class="google-search-preview bg-white">
					<div class="search-result">
						<a href="#" class="title">{{ selectedSeoData.title }}</a>
						<p class="url">{{ client.url }}/{{ selectedObject.slug }}</p>
						<p class="description">{{ selectedSeoData.description }}</p>
					</div>
				</div>
				<hr>
				<div class="form-group mb-3">
					<label for="ogTitle">OG Title</label>
					<input type="text" id="ogTitle" class="form-control" v-model="selectedSeoData.ogTitle" />
				</div>
				<div class="form-group mb-3">
					<div class="mb-2">
						<label for="ogDescription">OG Description</label>
					</div>
					<textarea id="ogDescription" rows="4" class="form-control" v-model="selectedSeoData.ogDescription" />
				</div>
				<div class="form-group mb-3">
					<label for="ogImage">OG Image</label>
					<input type="text" id="ogImage" class="form-control" v-model="selectedSeoData.ogImage" />
				</div>
				<div class="form-group mb-3">
					<label for="ogSiteName">OG Site Name</label>
					<input type="text" id="ogSiteName" class="form-control" v-model="selectedSeoData.ogSiteName" />
				</div>
				<div class="d-flex justify-content-end align-items-center">
					<button v-if="seoOperation == 'Create'" @click="storeSeo(selectedObject.id, 'App\\Models\\NewsNotice')" class="btn btn-success mb-3">
						<font-awesome-icon v-if="saveLoading" :icon="['fas', 'spinner']" :class="['fa-spin', 'ms-2']" :disabled="saveLoading" />{{ seoOperation }}
					</button>
					<button v-if="seoOperation == 'Update'" @click="updateSeo(selectedObject, 'App\\Models\\NewsNotice')" class="btn btn-primary mb-3">
						<font-awesome-icon v-if="saveLoading" :icon="['fas', 'spinner']" :class="['fa-spin', 'ms-2']" :disabled="saveLoading" />{{ seoOperation }}
					</button>
				</div>
				</div>
			</div>
			<div class="col-md-8 text-warning d-flex justify-content-center align-items-start mt-5" v-else>
				<h4>Select news notice in order to make changes</h4>
			</div>
		</div>

		<div class="modal" tabindex="-1" id="objectContentModal">
			<div class="modal-dialog modal-xl">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Content</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body" v-if="this.selectedObject">
						<div class="content-text" v-html="this.selectedObject.content"></div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close Preview</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
//import restApi from '../../../api/index'
import { Modal } from 'bootstrap'

export default {
	name: 'ClientSEO',
	data() {
		return {
			loading: false,
			saveLoading: false,
			aiLoading: false,
			aiDescriptionLoading: false,
			aiKeywordsLoading: false,
			client: {},
			selectedModel: 'page',
			search: '',
			seoObject: {
				data: {
					title: '',
					description: '',
					keywords: '' } },
			selectedSeoData: null,
			selectedObject: null,
			seoOperation: 'Create',
			pages: [],
			newsNotices: [],
			objectContentModal: null }
	},
	components: {},

	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
		filteredPages() {
			return this.filterBySearch(this.pages)
		},
		filteredNewsNotices() {
			return this.filterBySearch(this.newsNotices)
		} },

	mounted() {
		this.objectContentModal = new Modal('#objectContentModal')
	},

	created() {
		this.getClient()
		this.getPages()
		this.getNewsNotices()
	},
	methods: {
		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		getPages() {
			juniperApi
				.get(`/pages?client_id=${this.$route.params.id}&with_seo=true&context=edit&orderby=created_at`)
				.then(({ data }) => {
					this.pages = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		getNewsNotices() {
			juniperApi
				.get(`/news-notices?client_id=${this.$route.params.id}&with_seo=true&context=edit&per_page=10000&orderby=created_at`)
				.then(({ data }) => {
					console.log(data)
					this.newsNotices = data.post_data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		manageSeo(object) {
			this.selectedObject = object
			if (object.seo) {
				if (object.seo.data && typeof object.seo.data == 'string') {
					object.seo.data = JSON.parse(object.seo.data)
					this.selectedSeoData = object.seo.data
				} else {
					this.selectedSeoData = object.seo.data
				}
				this.seoOperation = 'Update'
			} else {
				this.selectedSeoData = {
					title: object.title,
					keywords: '',
					description: '',
					ogTitle: object.title,
					ogDescription: (object.content = object.content.replace(/<\/?[^>]+(>|$)/g, "").replace(/[\n\r\t]/gm, "").trim()).length > 100 ? object.content.slice(0, 100) + "..." : object.content,
					ogImage: object.featured_media ? object.featured_media.source_url : '',
					ogSiteName: this.client.name }
				this.seoOperation = 'Create'
			}
		},

		getSeo(objectId, model) {
			juniperApi
				.get(`/seo/${objectId}?client_id=${this.$route.params.id}&client_model=${model}`)
				.then(() => {})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		storeSeo(objectId, model) {
			let seoObject = {
				data: JSON.stringify(this.selectedSeoData),
				object_model: model,
				object_id: objectId,
				client_id: this.$route.params.id }
			this.saveLoading = true
			juniperApi
				.post(`/seo`, seoObject)
				.then(() => {
					this.getPages()
					this.getNewsNotices()
					this.saveLoading = false
					this.$swal.fire({
						icon: 'success',
						title: 'SEO Created',
						timer: 3000 })
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
					this.saveLoading = false
				})
		},

		updateSeo(object, model) {
			let seoObject = {
				data: JSON.stringify(this.selectedSeoData),
				object_model: model,
				object_id: object.id,
				client_id: this.$route.params.id }
			this.saveLoading = true
			juniperApi
				.put(`/seo/${object.seo.id}`, seoObject)
				.then(() => {
					this.getPages()
					this.getNewsNotices()
					this.saveLoading = false
					this.$swal.fire({
						icon: 'success',
						title: 'SEO Updated',
						timer: 3000 })
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
					this.saveLoading = false
				})
		},

		generateDescriptionWithAI() {
			this.aiDescriptionLoading = true
			let params = {
				type: 'description',
				prompt: this.selectedObject.content }
			juniperApi
				.post(`/seo/ai/propmpt`, params)
				.then(({ data }) => {
					this.selectedSeoData.description = data
					this.aiDescriptionLoading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
					this.aiDescriptionLoading = false
				})
		},

		generateKeywordsWithAI() {
			this.aiKeywordsLoading = true
			let params = {
				type: 'keywords',
				prompt: this.selectedObject.content }
			juniperApi
				.post(`/seo/ai/propmpt`, params)
				.then(({ data }) => {
					this.selectedSeoData.keywords = data
					this.aiKeywordsLoading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
					this.aiKeywordsLoading = false
				})
		},

		displayContent() {
			this.objectContentModal.show()
		},

		selectModel(model) {
			this.selectedModel = model
			this.search = ''
		},

		// Client-side filter by title or slug (data is already loaded)
		filterBySearch(list) {
			if (!Array.isArray(list)) return []
			const q = this.search.toLowerCase()
			if (!q) return list
			return list.filter(item => {
				const title = (item.title || '').toLowerCase()
				const slug = (item.slug || '').toLowerCase()
				return title.includes(q) || slug.includes(q)
			})
		} } }
</script>

<style scoped>
#clientDns {
	padding: 0 0 60px;
	margin-top: 0 !important;
}

.jc-view {
	width: 100%;
}

.content-text img {
	max-width: 100%;
}
/* Deprecation banner */
.seo-deprecated {
	display: flex;
	align-items: flex-start;
	font-size: 13px;
	color: #92400e;
	background: #fffbeb;
	border: 1px solid #fde68a;
	border-radius: 12px;
	padding: 10px 14px;
	line-height: 1.4;
}

.seo-top-note {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 12px 14px;
	color: #4b5563;
	font-size: 13px;
	background: transparent;
}

/* Model segmented control (settings language) */
.seo-models {
	display: inline-flex;
	gap: 0;
	background: #f3f4f6;
	padding: 4px;
	border-radius: 999px;
}

.seo-model-btn {
	border: 0;
	background: transparent;
	color: #6b7280;
	padding: 5px 16px;
	font-size: 13px;
	font-weight: 600;
	border-radius: 999px;
	transition: all 0.15s ease;
}

.seo-model-btn:hover:not(.active) {
	color: #374151;
}

.seo-model-btn.active {
	background: #fff;
	color: #111827;
	box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05);
}

.seo-list-panel {
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 12px;
}

.seo-section {
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 16px;
}

.seo-item {
	display: block;
	width: 100%;
	text-align: left;
	border: 1px solid #e6e8ec;
	background: #fff;
	padding: 10px 12px;
	border-radius: 10px;
	margin-bottom: 8px;
	cursor: pointer;
	transition: border-color 0.12s ease, background 0.12s ease;
}

.seo-item:hover {
	border-color: #d5d9e0;
	background: #f9fafb;
}

.seo-item.active {
	border-color: #bfdbfe;
	background: #e7f1ff;
}

.seo-item h5 {
	font-size: 14px;
}

.items-wrapper {
	height: 620px;
	overflow-y: auto;
	overflow-x: hidden;
}

/* Search field (settings language) */
.seo-search {
	position: relative;
}

.seo-search__icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 13px;
	pointer-events: none;
}

.seo-search__input {
	width: 100%;
	padding: 8px 32px 8px 34px;
	border: 1px solid #e6e8ec;
	border-radius: 8px;
	font-size: 13px;
	color: #374151;
	background: #fff;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.seo-search__input:focus {
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}

.seo-search__clear {
	position: absolute;
	right: 6px;
	top: 50%;
	transform: translateY(-50%);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background: #eef0f3;
	color: #6b7280;
	font-size: 11px;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
}

.seo-search__clear:hover {
	background: #e2e5ea;
	color: #1f2937;
}

.seo-empty {
	padding: 24px 12px;
	text-align: center;
	color: #8a909c;
	font-size: 13px;
}

/* Inputs / textareas → settings language */
.form-control {
	border: 1px solid #e6e8ec;
	border-radius: 8px;
	box-shadow: none;
}

.form-control:focus {
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}

.form-group label {
	font-size: 0.8rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.25rem;
}

/* Flat buttons (settings language) */
.btn-primary,
.btn-success,
.btn-secondary,
.btn-warning {
	border-radius: 8px;
	font-weight: 600;
	border: 1px solid #e6e8ec;
	background: transparent;
	color: #111827;
	box-shadow: none;
}

.btn-primary:hover,
.btn-success:hover,
.btn-secondary:hover,
.btn-warning:hover {
	background: #f3f5f9;
	border-color: #d1d5db;
	color: #111827;
}

.btn-primary,
.btn-success {
	border-color: #111827;
}

.btn-secondary {
	color: #6b7280;
}

/* "Generate with AI" — subtle amber accent */
.btn-warning {
	color: #b45309;
	border-color: #fcd9a3;
}

.btn-warning:hover {
	background: #fff7ed;
	border-color: #fbbf24;
	color: #b45309;
}

.btn:disabled {
	opacity: 0.5;
}
.google-search-preview {
	background-color: #fff;
	border-radius: 10px;
	border: 1px solid #e5e7eb;
	padding: 10px;
}

.google-search-preview .search-result {
	margin-bottom: 20px;

	padding: 10px;
	border-radius: 10px;
	border-radius: 5px;
}

.google-search-preview .title {
	font-size: 18px;
	font-weight: bold;
	color: #1a0dab;
	margin-bottom: 5px;
}

.google-search-preview .url {
	color: #006621;
	margin-bottom: 5px;
}

.google-search-preview .description {
	color: #545454;
}
</style>
